/**
 * OB depth refresh — operator acknowledgement policy (dashboard quote only).
 *
 * Design:
 * - Liquidity-only shaping (no YES / over-only): Tier‑2 checkbox follows ``needs_override_soft_gates_flag``;
 *   no‑YES liquidity never disables depth buttons solely because ``dominant_blocker`` is Tier‑2 first.
 * - Tier‑2 / innovation–posterior: `needs_override_soft_gates_flag` (true whenever soft-engine sensors
 *   fire, including alongside liquidity-shaped ladders) → operator checks → `override_soft_gates`.
 * - Non–Tier‑2 persist: with the checkbox, `persist_force_*` matches the button (two-sided vs one-sided)
 *   so writer overrides / gate breakdown waivers stay aligned with the OB refresh. Tier‑2 path uses
 *   `override_soft_gates` instead (same persist helper on the writer).
 * - TOB / scenario ``force_one_sided`` (and manual force one) make the product effectively one-sided even
 *   when posterior CI is still two-sided — Depth (one-sided) aligns without ack; two-sided needs persist.
 *
 * All flags here are derived only from `quote` — never from UI checkbox state — so ack logic stays stable.
 */

/**
 * @param {object | null | undefined} quote Market quote from dashboard API
 * @returns {object} policy snapshot
 */
export function getOrderBookDepthRefreshPolicy(quote) {
  const none = {
    refreshBlockedEntirely: false,
    refreshNeedsSoftFlag: false,
    liquidityDepthGate: false,
    oneSidedOverLiquidityGate: false,
    liquidityShapeRefreshExempt: false,
    gaEff: false,
    q2Eff: false,
    ciEff: false,
    dominantBlocker: null,
    allowDepthRefreshWhileQuoteBlocked: false,
    quoteEngineBlocksDepthReseed: true,
    isProductEffectivelyTwoSided: false,
    needsAckTwoSided: false,
    needsAckOneSided: false,
    manualPersistWorkflowApplies: false,
  };

  if (!quote) return none;

  const gb = quote.gate_breakdown || {};
  const lr = gb.liquidity_refresh;
  const ps = gb.product_shape;
  const obLiq = gb.ob_liquidity;
  const dominantBlocker = gb.dominant_blocker;

  const refreshBlockedEntirely = lr?.blocked_entirely === true;
  const refreshNeedsSoftFlag = lr?.needs_override_soft_gates_flag === true;
  const liquidityDepthGate = obLiq?.no_yes_depth === true;
  const oneSidedOverLiquidityGate = obLiq?.one_sided_over_only === true;
  const liquidityShapeRefreshExempt = liquidityDepthGate || oneSidedOverLiquidityGate;

  const soft = gb.soft_engine || {};
  const mi = gb.manual_intervention_flags || {};
  const tobForceOne =
    ps?.tob_force_one_sided === true || soft?.tob_margin?.force_one_sided === true;
  const manualForceOne = mi?.force_one_sided === true;
  const manualForceTwo = mi?.force_two_sided === true;
  /** Engine/manual one-way wins over CI when deciding which depth button is shape-aligned. */
  const forceProductOneSided = !manualForceTwo && (manualForceOne || tobForceOne);

  const gaEff = Boolean(quote.gate_allowed);
  const q2Eff = Boolean(quote.quote_two_sided) && !forceProductOneSided;
  const ciEff = ps?.ci_two_sided === true;

  // When synthetic YES/over is depleted, depth refresh must stay available even if Tier‑2 still
  // wins ``dominant_blocker`` ordering (soft_engine is checked before ob_no_yes_liquidity).
  const allowDepthRefreshWhileQuoteBlocked =
    liquidityDepthGate || dominantBlocker === 'ob_no_yes_liquidity';
  const quoteEngineBlocksDepthReseed = Boolean(!quote.gate_allowed && !allowDepthRefreshWhileQuoteBlocked);

  let isProductEffectivelyTwoSided;
  if (manualForceTwo) {
    isProductEffectivelyTwoSided = true;
  } else if (forceProductOneSided) {
    // TOB under-exhaust / scenario / manual one-sided — not CI band.
    isProductEffectivelyTwoSided = false;
  } else if (quote.gate_allowed) {
    isProductEffectivelyTwoSided = liquidityShapeRefreshExempt
      ? Boolean(ps?.ci_two_sided)
      : Boolean(quote.quote_two_sided);
  } else {
    isProductEffectivelyTwoSided = Boolean(ps?.ci_two_sided);
  }

  const tier2Ack = refreshNeedsSoftFlag;

  /** Mirrors prior OrderBookTerminal OR of `twoSidedNeedsAck` + cross-shape widen (cross-shape ignored liquidity exempt). */
  const needsAckTwoSided =
    tier2Ack ||
    (gaEff && !q2Eff && !liquidityShapeRefreshExempt) ||
    (!ciEff && !liquidityShapeRefreshExempt && q2Eff) ||
    !isProductEffectivelyTwoSided;

  /** Mirrors prior `oneSidedNeedsAck` + cross-shape narrow (cross-shape ignored liquidity exempt). */
  const needsAckOneSided =
    tier2Ack || (gaEff && q2Eff && !liquidityShapeRefreshExempt) || isProductEffectivelyTwoSided;

  return {
    refreshBlockedEntirely,
    refreshNeedsSoftFlag,
    liquidityDepthGate,
    oneSidedOverLiquidityGate,
    liquidityShapeRefreshExempt,
    gaEff,
    q2Eff,
    ciEff,
    dominantBlocker,
    allowDepthRefreshWhileQuoteBlocked,
    quoteEngineBlocksDepthReseed,
    isProductEffectivelyTwoSided,
    needsAckTwoSided,
    needsAckOneSided,
    // Over-only ladders often skip Tier‑2 / cross-shape ack math but still need an optional
    // persistence path when the operator checks the box (``persist_force_two_sided``).
    manualPersistWorkflowApplies:
      needsAckTwoSided || needsAckOneSided || oneSidedOverLiquidityGate,
  };
}

/**
 * Allow persist_force_* when quote is blocked but operator is aligning refresh to natural product shape.
 * Mirrors Trading Dash ``needsShapePersistOverride`` (gate API leave-open + force_*).
 */
function needsBlockedShapePersist(policy, twoSidedMode) {
  if (policy.gaEff || policy.liquidityDepthGate) return false;
  if (twoSidedMode) return !policy.isProductEffectivelyTwoSided;
  return policy.isProductEffectivelyTwoSided;
}

/**
 * POST `/order-book/refresh-depth` body aligned with writer `refresh_order_book_depth`.
 *
 * @param {'two_sided' | 'one_sided'} mode
 * @param {ReturnType<typeof getOrderBookDepthRefreshPolicy>} policy
 * @param {boolean} manualAck operator checkbox after confirm
 * @param {string | undefined} actedBy
 */
export function buildOrderBookRefreshDepthPayload(mode, policy, manualAck, actedBy) {
  const tier2 = Boolean(policy.refreshNeedsSoftFlag);
  const ack = Boolean(manualAck);
  const workflow = Boolean(policy.manualPersistWorkflowApplies);
  /** Tier‑2 still requires an explicit checkbox; routine refreshes (no workflow) imply consent to persist shape. */
  const effectiveAck = ack || !workflow;
  const two = mode === 'two_sided';
  const payload = {
    mode: two ? 'two_sided' : 'one_sided',
    override_soft_gates: ack && tier2,
    // Writer: `refresh_order_book_depth` → `_persist_manual_shaping_for_ob_tier2_refresh` when set.
    // Omit cross-shape-only filters so shape-aligned refreshes still record force_two / force_one overrides.
    persist_force_two_sided:
      two && effectiveAck && !tier2 && (policy.gaEff || needsBlockedShapePersist(policy, true)),
    persist_force_one_sided:
      !two && effectiveAck && !tier2 && (policy.gaEff || needsBlockedShapePersist(policy, false)),
  };
  if (actedBy != null && String(actedBy).trim()) {
    payload.acted_by = String(actedBy).trim();
  }
  return payload;
}
